import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { DynamicFormRenderer, FormEngineProvider } from '@client/components/dynamic-form';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { createFormStore, type FormStore } from '@client/stores/FormEngine';
import { trpc } from '@client/trpc/client';
import type { DynamicFormSchema, FormResponse } from '@shared/services/form-engine';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Typography } from 'antd';
import { Ban, CheckCircle, Clock, FileText, ShieldX, XCircle } from 'lucide-react';
import { type PropsWithChildren, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/attestation/$token')({
    params: {
        parse: (params) => ({
            token: z.string().uuid().parse(params.token),
        }),
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <AttestationFormPage />
        </PageController>
    );
}

function AttestationStatusCard({ children }: PropsWithChildren) {
    return <Card className="mx-auto w-full max-w-lg shadow-lg">{children}</Card>;
}

function AttestationFormPage() {
    const { token } = Route.useParams();
    const orgId = useOrgId();
    const formStartTime = useRef(Date.now());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Auth is handled by RequireAuth in parent route (~attestation/~route.tsx)
    // which properly redirects to login and returns user here after authentication
    const { data: attestation, isLoading, error } = trpc.userAttestation.getAttestationByToken.useQuery({ token });

    const submitMutation = trpc.userAttestation.submitUserAttestation.useMutation({
        onSuccess: () => {
            setIsSubmitted(true);
            message.success('Attestation submitted successfully');
        },
        onError: (err) => {
            message.error(err.message || 'Failed to submit attestation');
            setIsSubmitting(false);
        },
    });

    const formStore: FormStore | null = useMemo(() => {
        if (!attestation?.formSchema?.schema) return null;

        try {
            const schema = attestation.formSchema.schema as DynamicFormSchema;
            return createFormStore({
                schema,
                initialValues: {},
                autoValidate: true,
            });
        } catch (err) {
            console.error('Failed to create form store:', err);
            return null;
        }
    }, [attestation?.formSchema?.schema]);

    const handleSubmit = async (response: FormResponse) => {
        if (!attestation) return;

        setIsSubmitting(true);
        const submissionDuration = Date.now() - formStartTime.current;

        await submitMutation.mutateAsync({
            token,
            submission: {
                values: response.values,
                submissionDuration,
                pageHistory: response.pageHistory,
            },
        });
    };

    if (isLoading) {
        return (
            <Card key="attestation-pending" className="shadow-lg">
                <Loading size="large" description="Loading attestation..." />
            </Card>
        );
    }

    if (error) {
        const isUnauthorized = error.data?.code === 'UNAUTHORIZED';
        const isForbidden = error.data?.code === 'FORBIDDEN';
        const isNotFound = error.data?.code === 'NOT_FOUND';

        if (isUnauthorized) {
            return (
                <Card key="attestation-pending" className="shadow-lg">
                    <Loading size="large" />
                </Card>
            );
        }

        return (
            <AttestationStatusCard key="attestation-status">
                <div className="py-8 text-center">
                    <div className="mb-6 flex justify-center">
                        <div
                            className={`flex h-20 w-20 items-center justify-center rounded-full ${
                                isForbidden ? 'bg-orange-100' : 'bg-red-100'
                            }`}
                        >
                            {isForbidden ? (
                                <ShieldX className="text-orange-600" size={40} />
                            ) : (
                                <XCircle className="text-red-600" size={40} />
                            )}
                        </div>
                    </div>
                    <Title level={3} className="mb-2">
                        {isForbidden
                            ? 'Access Denied'
                            : isNotFound
                              ? 'Attestation Not Found'
                              : 'Error Loading Attestation'}
                    </Title>
                    <Text type="secondary" className="block">
                        {isForbidden
                            ? 'You are not the intended recipient of this attestation. Please ensure you are logged in with the correct account.'
                            : error.message || 'The attestation link may have expired or is invalid.'}
                    </Text>
                    <Button.Link
                        to="/console/$orgId"
                        params={{ orgId }}
                        variant="secondary"
                        className="mt-6 inline-block text-muted-foreground text-sm hover:text-foreground"
                    >
                        ← Back to home
                    </Button.Link>
                </div>
            </AttestationStatusCard>
        );
    }

    if (!attestation) {
        return (
            <AttestationStatusCard key="attestation-status">
                <div className="py-8 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
                            <Clock className="text-orange-600" size={40} />
                        </div>
                    </div>
                    <Title level={3} className="mb-2">
                        Attestation Not Available
                    </Title>
                    <Text type="secondary" className="block">
                        Unable to load attestation details.
                    </Text>
                    <Button.Link
                        to="/console/$orgId"
                        params={{ orgId }}
                        variant="secondary"
                        className="mt-6 inline-block text-muted-foreground text-sm hover:text-foreground"
                    >
                        ← Back to home
                    </Button.Link>
                </div>
            </AttestationStatusCard>
        );
    }

    // Cancelled state - attestation was cancelled by admin
    if (attestation.isCancelled) {
        return (
            <AttestationStatusCard key="attestation-status">
                <div className="py-8 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                            <Ban className="text-muted-foreground" size={40} />
                        </div>
                    </div>
                    <Title level={3} className="mb-2">
                        Attestation Cancelled
                    </Title>
                    <Text type="secondary" className="block">
                        This attestation has been cancelled and no action is required.
                    </Text>
                    <Text type="secondary" className="mt-4 block text-sm">
                        If you believe this is an error, please contact your administrator.
                    </Text>
                    <Button.Link
                        to="/console/$orgId"
                        params={{ orgId }}
                        variant="secondary"
                        className="mt-6 inline-block text-muted-foreground text-sm hover:text-foreground"
                    >
                        ← Back to home
                    </Button.Link>
                </div>
            </AttestationStatusCard>
        );
    }

    // Already submitted state
    if (attestation.isSubmitted || isSubmitted) {
        return (
            <AttestationStatusCard key="attestation-status">
                <div className="py-8 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                            <CheckCircle className="text-green-600" size={40} />
                        </div>
                    </div>
                    <Title level={3} className="mb-2">
                        {isSubmitted ? 'Attestation Submitted' : 'Attestation Already Submitted'}
                    </Title>
                    <Text type="secondary" className="mb-4 block">
                        {attestation.submittedAt
                            ? `You submitted this attestation on ${new Date(attestation.submittedAt).toLocaleDateString()}`
                            : 'This attestation has been submitted successfully.'}
                    </Text>
                    <Text type="secondary" className="block text-sm">
                        Thank you for completing the {attestation.formSchema.title} attestation.
                    </Text>
                </div>
            </AttestationStatusCard>
        );
    }

    // Expired/Overdue state (when not allowed to submit)
    if (!attestation.canSubmit) {
        return (
            <AttestationStatusCard key="attestation-status">
                <div className="py-8 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
                            <Clock className="text-orange-600" size={40} />
                        </div>
                    </div>
                    <Title level={3} className="mb-2">
                        Attestation Unavailable
                    </Title>
                    <Text type="secondary" className="block">
                        This attestation can no longer be submitted. Please contact your administrator.
                    </Text>
                    <Button.Link
                        to="/console/$orgId"
                        params={{ orgId }}
                        variant="secondary"
                        className="mt-6 inline-block text-muted-foreground text-sm hover:text-foreground"
                    >
                        ← Back to home
                    </Button.Link>
                </div>
            </AttestationStatusCard>
        );
    }

    // Form schema not available
    if (!formStore) {
        return (
            <AttestationStatusCard key="attestation-status">
                <div className="py-8 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                            <XCircle className="text-red-600" size={40} />
                        </div>
                    </div>
                    <Title level={3} className="mb-2">
                        Form Not Available
                    </Title>
                    <Text type="secondary" className="block">
                        Unable to load the attestation form. Please contact your administrator.
                    </Text>
                    <Button.Link
                        to="/console/$orgId"
                        params={{ orgId }}
                        variant="secondary"
                        className="mt-6 inline-block text-muted-foreground text-sm hover:text-foreground"
                    >
                        ← Back to home
                    </Button.Link>
                </div>
            </AttestationStatusCard>
        );
    }

    // Main form view
    return (
        <div key="attestation-form">
            {/* Header Card */}
            <Card className="mb-6 shadow-lg">
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-500">
                        <FileText className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                        <Title level={3} className="mb-1">
                            {attestation.formSchema.title}
                        </Title>
                        <Text type="secondary">{attestation.org.name}</Text>

                        {attestation.formSchema.description && (
                            <Paragraph type="secondary" className="mt-2 mb-0">
                                {attestation.formSchema.description}
                            </Paragraph>
                        )}
                    </div>
                </div>

                {/* Due date and recipient info */}
                <div className="mt-4 flex flex-wrap gap-6 border-separator border-t pt-4">
                    <div>
                        <Text type="secondary" className="text-xs uppercase tracking-wide">
                            Due Date
                        </Text>
                        <div className="mt-1">
                            <Text strong>{new Date(attestation.dueAt).toLocaleDateString()}</Text>
                        </div>
                    </div>
                    <div>
                        <Text type="secondary" className="text-xs uppercase tracking-wide">
                            Recipient
                        </Text>
                        <div className="mt-1">
                            <Text strong>{attestation.recipient.fullName || attestation.recipient.email}</Text>
                        </div>
                    </div>
                </div>

                {attestation.customMessage && (
                    <Alert
                        type="info"
                        title="Message from sender"
                        description={attestation.customMessage}
                        className="mt-4"
                        showIcon
                    />
                )}
            </Card>

            {/* Form Card */}
            <Card className="relative shadow-lg">
                <FormEngineProvider store={formStore}>
                    <DynamicFormRenderer
                        onSubmit={handleSubmit}
                        submitLabel="Submit Attestation"
                        showNavigation={true}
                        showValidationErrors={false}
                    />
                </FormEngineProvider>

                {isSubmitting && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
                        <Loading size="large" description="Submitting..." />
                    </div>
                )}
            </Card>

            {/* Footer */}
            <div className="mt-6 text-center">
                <Text type="secondary" className="text-xs">
                    Powered by Curium
                </Text>
            </div>
        </div>
    );
}
